/* ---------- Read-only Segment Timeline (clean custom palette) ---------- */
import React from 'react';
import { clamp, abbr } from '@/util';

const safeInside = (start, end) => {
  const pad = 0.1; // 100ms inside
  let t = start + pad;
  if (!Number.isFinite(t) || t >= end) t = (start + end) / 2;
  return clamp(t, start + 1e-3, Math.max(start + 1e-3, end - 1e-3));
};

export const ReadOnlySegmentTimeline = React.memo(
  function ReadOnlySegmentTimeline({
    steps = [],
    durationSec = 0,
    currentTimeSec = 0,
    onSeek = () => {},
    onSelectIndex = undefined,
    setIsPausedBySegment,
    v,
  }) {
    const dur = Math.max(0.001, durationSec || 0.001);
    const [isNarrow, setIsNarrow] = React.useState(false);

    React.useEffect(() => {
      const mq = window.matchMedia('(max-width: 640px)');
      const update = () => setIsNarrow(mq.matches);
      update();
      mq.addEventListener?.('change', update);
      return () => mq.removeEventListener?.('change', update);
    }, []);

    const segments = React.useMemo(() => {
      const ordered = [...steps]
        .map((s, i) => ({ ...s, _origIndex: i }))
        .sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0));
      return ordered.map((s, idx) => {
        const startSec = clamp((s.start_ms - 250 ?? 0) / 1000, 0, dur);
        const endSec = clamp(
          (s.end_ms - 250 ?? startSec) / 1000,
          startSec,
          dur
        );
        const active = currentTimeSec >= startSec && currentTimeSec < endSec;
        return {
          idx,
          startSec,
          endSec,
          title: s.title || `Step ${idx + 1}`,
          active,
        };
      });
    }, [steps, currentTimeSec, dur]);

    return (
      <div className="w-full">
        <div
          className="relative h-8 w-full select-none md:h-10"
          style={{ backgroundColor: '#EAEAED', borderRadius: 8 }}
        >
          {segments.map((seg) => {
            const leftPct = (seg.startSec / dur) * 100;
            const widthPct = ((seg.endSec - seg.startSec) / dur) * 100;
            const baseLabel = isNarrow
              ? abbr(seg.title, 0)
              : abbr(seg.title, 22);
            const labelText = `${seg.idx + 1}${
              isNarrow ? '' : `. ${baseLabel}`
            }`;

            return (
              <button
                key={seg.idx}
                type="button"
                data-scorm-instruction-segment={seg.idx}
                className="absolute top-0 h-8 overflow-hidden transition md:h-10"
                style={{
                  left: `${leftPct}%`,
                  width: `${Math.max(0.75, widthPct)}%`,
                  backgroundColor: seg.active ? '#3257FC' : '#c5c5c8',
                  boxSizing: 'border-box',
                  borderLeft: seg.idx === 0 ? 'none' : '1px solid #EAEAED',
                  borderTop: '1px solid #EAEAED',
                  borderBottom: '1px solid #EAEAED',
                  borderRadius: 6,
                }}
                onClick={() => {
                  setIsPausedBySegment(false);
                  v?.play?.();
                  onSeek(safeInside(seg.startSec, seg.endSec));
                  if (typeof onSelectIndex === 'function')
                    onSelectIndex(seg.idx);
                }}
                aria-current={seg.active ? 'true' : undefined}
              >
                <div
                  className="absolute inset-0 flex items-center justify-start text-[11px] font-medium"
                  style={{
                    right: 6,
                    paddingLeft: 6,
                    paddingRight: 6, // ← added spacing so ellipsis doesn’t touch edge
                    color: seg.active ? '#fff' : '#000',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                  }}
                >
                  {labelText}
                </div>
              </button>
            );
          })}

          <div
            className="absolute w-[2px]"
            data-scorm-instruction-playhead
            style={{
              top: 0,
              height: '100%',
              left: `${(clamp(currentTimeSec, 0, dur) / dur) * 100}%`,
              backgroundColor: '#000',
              zIndex: 20,
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
    );
  }
);
